import { useState } from "react";
import { Star, Camera, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const MAX_PHOTO_DIM = 480; // px — client-side resize keeps DB documents small

function resizeImageToBase64(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, MAX_PHOTO_DIM / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82).split(",")[1]);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ReviewForm({ fields = ["name", "car", "city"], onSubmit, submitLabel = "Submit review" }) {
  const [form, setForm] = useState({ name: "", car: "", city: "", designation: "", rating: 5, text: "" });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handlePhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    const b64 = await resizeImageToBase64(file);
    setPhotoBase64(b64);
    setPhotoPreview(`data:image/jpeg;base64,${b64}`);
  }

  function removePhoto() {
    setPhotoBase64(null);
    setPhotoPreview(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.text.trim() || form.text.trim().length < 10) return;
    setSubmitting(true);
    try {
      await onSubmit({ ...form, photo: photoBase64 });
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-3xl border border-white/5 bg-surface p-8 text-center" data-testid="review-form-success">
        <p className="font-serif text-2xl text-white">Thank you!</p>
        <p className="mt-2 text-silver/60 text-sm">Your review has been submitted and will appear once approved.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-3xl border border-white/5 bg-surface p-7 md:p-8 space-y-5" data-testid="review-form">
      {fields.includes("name") && (
        <div>
          <label className="text-xs text-silver/50 uppercase tracking-wider">Your name</label>
          <Input required value={form.name} onChange={set("name")} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-input-name" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        {fields.includes("car") && (
          <div>
            <label className="text-xs text-silver/50 uppercase tracking-wider">Car bought</label>
            <Input value={form.car} onChange={set("car")} placeholder="e.g. Hyundai Creta SX(O)" className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-input-car" />
          </div>
        )}
        {fields.includes("city") && (
          <div>
            <label className="text-xs text-silver/50 uppercase tracking-wider">City</label>
            <Input value={form.city} onChange={set("city")} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-input-city" />
          </div>
        )}
      </div>
      <div>
        <label className="text-xs text-silver/50 uppercase tracking-wider">Designation <span className="normal-case text-silver/35">(optional — e.g. "Software Engineer")</span></label>
        <Input value={form.designation} onChange={set("designation")} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-input-designation" />
      </div>
      <div>
        <label className="text-xs text-silver/50 uppercase tracking-wider">Your photo <span className="normal-case text-silver/35">(optional)</span></label>
        {photoPreview ? (
          <div className="mt-2 flex items-center gap-3">
            <img src={photoPreview} alt="Preview" className="w-14 h-14 rounded-full object-cover border border-white/10" />
            <button type="button" onClick={removePhoto} className="inline-flex items-center gap-1 text-xs text-silver/60 hover:text-white" data-testid="review-photo-remove">
              <X className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        ) : (
          <label className="mt-2 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-xs text-silver/70 hover:text-white hover:border-white/30 cursor-pointer transition-colors" data-testid="review-photo-upload">
            <Camera className="w-3.5 h-3.5" /> Upload a photo
            <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
          </label>
        )}
      </div>
      <div>
        <label className="text-xs text-silver/50 uppercase tracking-wider">Rating</label>
        <div className="mt-1.5 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={() => setForm((f) => ({ ...f, rating: n }))} data-testid={`review-star-${n}`}>
              <Star className={`w-7 h-7 ${n <= form.rating ? "fill-[#F43F5E] text-[#F43F5E]" : "text-silver/25"}`} />
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs text-silver/50 uppercase tracking-wider">Your review</label>
        <Textarea
          required
          minLength={10}
          rows={4}
          value={form.text}
          onChange={set("text")}
          placeholder="How was your experience with your Saarthi?"
          className="mt-1.5 bg-void border-white/10 text-white"
          data-testid="review-input-text"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3.5 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.01] transition-transform disabled:opacity-60"
        data-testid="review-submit-btn"
      >
        {submitting ? "Submitting…" : submitLabel}
      </button>
    </form>
  );
}
